// apps/web/src/lib/foodDraft.ts
// What FoodSheet produces when you press Save, and the default way to persist
// it.
//
// The sheet builds the draft but no longer performs the write itself: whoever
// opened it decides what "saved" means. AddFood and the barcode scanner await
// the network so a failure can be shown in the sheet; Today applies the draft
// locally first and writes behind the scenes. Same draft, same persistence
// call, different timing.
import { api } from "./api";
import type { FoodEntry, MealSlot } from "@shared/types";

export interface FoodDraft {
  /** Present when editing — the row to patch. */
  id?: number;
  /** Idempotency key for adds — minted automatically when absent, so a
   *  network retry or offline-queue replay can never write the row twice
   *  (a scanned product once landed three times; owner bug report). */
  client_id?: string;
  eaten_on: string;
  source: "plan" | "custom" | "quick";
  plan_meal_slot: MealSlot | null;
  name: string;
  calories: number;
  /** Detail amounts are Pro (owner, 2026-08-07). Omitted (undefined) fields
   *  are stripped before the write — a free edit patches name/calories only,
   *  so an entry's stored details survive untouched. Explicit null clears. */
  protein_g?: number | null;
  carbs_g?: number | null;
  fat_g?: number | null;
  sugar_g?: number | null;
  fiber_g?: number | null;
  micros?: Record<string, number> | null;
}

/** Everything the write sends — the draft minus its own row id. */
export function draftFields(draft: FoodDraft): Omit<FoodDraft, "id"> {
  const { id: _rowId, ...fields } = draft;
  // Drop undefined keys entirely: the PATCH body must not carry them, and an
  // optimistic spread over an existing entry must not clobber stored values.
  return Object.fromEntries(
    Object.entries(fields).filter(([, v]) => v !== undefined),
  ) as Omit<FoodDraft, "id">;
}

/** Persist a draft. Returns the row id (negative only when the write was
 *  queued offline and the real id does not exist yet). */
export async function saveFoodDraft(draft: FoodDraft): Promise<number> {
  if (draft.id !== undefined) {
    await api.updateFood(draft.id, draftFields(draft));
    return draft.id;
  }
  const withId = draft.client_id ? draft : { ...draft, client_id: crypto.randomUUID() };
  const r = await api.addFood(draftFields(withId));
  return r.id;
}

/** The draft as a brand-new row in the day's list, for optimistic rendering.
 *  Only for adds — an edit must keep the entry's original `eaten_at`, which is
 *  not part of the draft and is never sent by the update. */
export function draftToEntry(draft: FoodDraft, id: number): FoodEntry {
  return {
    id,
    eaten_at: Math.floor(Date.now() / 1000),
    protein_g: draft.protein_g ?? null,
    carbs_g: draft.carbs_g ?? null,
    fat_g: draft.fat_g ?? null,
    sugar_g: draft.sugar_g ?? null,
    fiber_g: draft.fiber_g ?? null,
    micros: draft.micros ?? null,
    ...draftFields(draft),
  } as FoodEntry;
}
